import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Instagram, Youtube, Facebook } from "lucide-react";
import Header from "@/components/essex/Header";
import Footer from "@/components/essex/Footer";
import { SecureDateCTA } from "@/components/essex/SecureDateCTA";
import { useModal } from "@/components/essex/ModalContext";

const teamMembers = [
  {
    name: "Alex Moore",
    role: "Head Videographer",
    image: "https://static.wixstatic.com/media/1fe187_c96ade0f4d2f43c6bc7bd9a10bf59fee~mv2.jpg/v1/crop/x_1260,y_0,w_3666,h_4126/fill/w_400,h_450,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Alex-TEAM-PHOTO-WVE.jpg",
  },
  {
    name: "Ellis Goward",
    role: "Videographer & Photographer",
    image: "https://static.wixstatic.com/media/1fe187_451802347d194e08b9aff9bef88309b1~mv2.jpg/v1/crop/x_1196,y_0,w_4037,h_4541/fill/w_400,h_450,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/ELLIS-TEAM-PHOTO-WVE.jpg",
  },
  {
    name: "Shannon Barrett",
    role: "Head Editor & Videographer",
    image: "https://static.wixstatic.com/media/1fe187_ea4823a578234aaa96c2d6a1fe27d371~mv2.jpg/v1/crop/x_1426,y_0,w_4151,h_4672/fill/w_400,h_450,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/SHANNON-TEAM-PHOTO-WVE.jpg",
  },
  {
    name: "Amie Evans",
    role: "Videographer",
    image: "https://static.wixstatic.com/media/1fe187_b445f84e474d48caaecdc648dc20d990~mv2.jpg/v1/crop/x_1246,y_0,w_3803,h_4277/fill/w_400,h_450,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/AMIE-TEAM-PHOTO-WVE.jpg",
  },
  {
    name: "Andy Thomas",
    role: "Photographer",
    image: "https://static.wixstatic.com/media/1fe187_b4bcb01ce0fd4d69bd6112e2c785b2b1~mv2.jpg/v1/crop/x_1259,y_0,w_3663,h_4123/fill/w_400,h_450,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/ANDY-TEAM-PHOTO-WVE.jpg",
  },
  {
    name: "Lily Wright",
    role: "Videographer",
    image: "https://static.wixstatic.com/media/1fe187_d630ef3202814fab9a919b8a8c87e28d~mv2.jpg/v1/crop/x_1365,y_0,w_4259,h_4791/fill/w_400,h_450,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/LILY-TEAM-PHOTO-WVE.jpg",
  },
];

export default function About() {
  const { openModal } = useModal();

  return (
    <div data-testid="about-page" className="bg-[#0A0908] min-h-screen">
      <Helmet>
        <title>{"About Us | Wedding Video Essex"}</title>
        <meta name="description" content="Meet the Wedding Video Essex team. Over 10 years of experience, 700+ happy couples, and 7x Wedding Awards Winners. Photo and video that feels real." />
        <link rel="canonical" href="https://essex.weddingvideo.co.uk/about" />
      </Helmet>
      <Header />

      <main>
        {/* Hero Banner */}
        <section className="relative pt-24 pb-16 md:pt-32 md:pb-24">
          <div className="absolute inset-0 overflow-hidden">
            <img
              src="https://static.wixstatic.com/media/1fe187_d6b7d99338ef48a28e6c5cef9aa966d7f000.jpg/v1/fill/w_1904,h_451,al_t,q_85,usm_0.33_1.00_0.00,enc_avif,quality_auto/1fe187_d6b7d99338ef48a28e6c5cef9aa966d7f000.jpg"
              alt="Wedding Video Essex team on location"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A0908]/60 via-[#0A0908]/40 to-[#0A0908]" />
          </div>
          <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4"
            >
              About Us
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="about-heading"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-[#F5F5F0]"
            >
              The people behind the cameras
            </motion.h1>
          </div>
        </section>

        {/* Alex Intro Section */}
        <section data-testid="about-intro" className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-5 gap-10 md:gap-16 items-start">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="md:col-span-2"
              >
                <img
                  src="https://static.wixstatic.com/media/1fe187_8bb3b3fc11c949f7bb3fccd9fe868529~mv2.jpg/v1/crop/x_687,y_0,w_5302,h_4452/fill/w_500,h_420,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WVE-TEAM-PHOTO.jpg"
                  alt="The Wedding Video Essex team"
                  data-testid="about-team-photo"
                  className="w-full aspect-[5/4] object-cover border border-white/[0.06]"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="md:col-span-3"
              >
                <h2 className="font-serif text-2xl sm:text-3xl tracking-tight font-light text-[#F5F5F0] mb-6">
                  Hey, I'm Alex & welcome to Wedding Video Essex!
                </h2>
                <div className="space-y-4 text-sm text-[#A19D94] font-sans leading-relaxed">
                  <p>
                    Over the past 10 years, we've helped over 700 happy couples capture their memories for a lifetime.
                  </p>
                  <p>
                    21 venues in Essex recommend us, and we're 7x Wedding Awards Winners. More importantly, our couples always leave us fantastic 5 star reviews.
                  </p>
                  <p>
                    Most couples we work with aren't "comfortable" in front of the camera, and that's understandable. Our style captures natural, authentic moments. No awkward poses, just real memories.
                  </p>
                  <p>
                    We offer all inclusive Wedding Video & Photo Packages, ensuring you don't have to choose between stunning photos or an emotional film. Your entire day is captured by a team that works perfectly together.
                  </p>
                  <p>
                    Plus, there are no hidden fees. Our cinematic storytelling takes you back to your wedding day.
                  </p>
                  <p>
                    Our films aren't just videos. They're timeless memories crafted to bring you back to your wedding day every time you press play.
                  </p>
                  <p className="text-[#F5F5F0] font-medium">
                    Join hundreds of happy couples who have already captured their memories for a lifetime.
                  </p>
                </div>

                <button
                  onClick={openModal}
                  data-testid="about-cta-btn"
                  className="mt-8 px-8 py-3 bg-[#C2A878] text-[#0A0908] text-sm font-sans font-medium tracking-wider uppercase hover:bg-[#d4bc92] transition-colors"
                >
                  Talk to Us About Your Day
                </button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Team Grid */}
        <section data-testid="about-team-section" className="py-16 md:py-24 bg-[#080706]">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-3">
                Meet the Crew
              </p>
              <h2
                data-testid="team-heading"
                className="font-serif text-2xl sm:text-3xl tracking-tight font-light text-[#F5F5F0]"
              >
                The WVE Team
              </h2>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {teamMembers.map((member, i) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`team-member-${i}`}
                  className="group"
                >
                  <div className="relative aspect-[4/4.5] overflow-hidden border border-white/[0.06]">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-sm font-sans font-medium text-[#F5F5F0]">{member.name}</p>
                      <p className="text-xs font-sans text-[#C2A878]">{member.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Social Links */}
        <section className="py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-xl sm:text-2xl tracking-tight font-light text-[#F5F5F0] mb-6">
                Follow our work
              </h2>
              <div className="flex items-center justify-center gap-6">
                <a
                  href="https://www.instagram.com/weddingvideoessex"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-social-instagram"
                  className="flex items-center gap-2 text-sm text-[#A19D94] hover:text-[#C2A878] transition-colors font-sans"
                >
                  <Instagram size={18} />
                  Instagram
                </a>
                <a
                  href="https://youtube.com/playlist?list=PL9tAB3F7qycVDBTIZl8YXCoPdTnS3oU2O"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-social-youtube"
                  className="flex items-center gap-2 text-sm text-[#A19D94] hover:text-[#C2A878] transition-colors font-sans"
                >
                  <Youtube size={18} />
                  YouTube
                </a>
                <a
                  href="https://www.facebook.com/essexweddingvideo"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-social-facebook"
                  className="flex items-center gap-2 text-sm text-[#A19D94] hover:text-[#C2A878] transition-colors font-sans"
                >
                  <Facebook size={18} />
                  Facebook
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        <SecureDateCTA
          heading="Ready to chat about your wedding?"
          subtext="We only take a limited number of weddings each month. Pop your details in and we'll get back to you within 24 hours."
        />
      </main>
      <Footer />
    </div>
  );
}
